/**
 * Widget Editor Modal
 * Allows editing widget configuration including endpoint selection,
 * column selection for tables (LN-style personalization), and conditional formatting
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X, Save, Database, RefreshCw, Plus, Trash2,
  Palette, Settings, Zap, AlertCircle, Search,
  ChevronUp, ChevronDown, ChevronRight, ChevronLeft,
  Columns, CheckSquare, Square, ChevronsRight, ChevronsLeft,
  GripVertical, ChevronsUp, ChevronsDown, Calendar, Clock,
  Filter, Code, Link2
} from 'lucide-react';
import { Widget, WidgetType, WidgetConfig, ConditionalFormat, ColumnFormat, TableColumn, ExpandRelation } from '../../types';
import { endpointsApi, widgetsApi, dataApi, odataServicesApi } from '../../api/client';
import { useODataSchema } from '../../hooks/useODataSchema';
import { ServerFilterPanel } from '../ui/ServerFilterPanel';
import { RelationsPanel } from '../ui/RelationsPanel';
import { ODataQueryBuilder, type ODataFilter, type ODataExpand } from '../../services/odataService';

interface WidgetEditorModalProps {
  widget: Widget;
  onClose: () => void;
  onSaved: () => void;
}

const WIDGET_TYPES: { value: WidgetType; label: string }[] = [
  { value: 'table', label: 'Data Table' },
  { value: 'kpi', label: 'KPI Card' },
  { value: 'chart_bar', label: 'Bar Chart' },
  { value: 'chart_line', label: 'Line Chart' },
  { value: 'chart_pie', label: 'Pie Chart' },
  { value: 'status_grid', label: 'Status Grid' },
  { value: 'status_indicator', label: 'Status Indicator' },
];

const AGGREGATIONS = [
  { value: 'count', label: 'Count' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
];

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'Equals (=)' },
  { value: 'not_equals', label: 'Not Equals (≠)' },
  { value: 'greater', label: 'Greater Than (>)' },
  { value: 'greater_equal', label: 'Greater or Equal (≥)' },
  { value: 'less', label: 'Less Than (<)' },
  { value: 'less_equal', label: 'Less or Equal (≤)' },
  { value: 'between', label: 'Between' },
  { value: 'contains', label: 'Contains' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'is_not_empty', label: 'Is Not Empty' },
];

const COLUMN_FORMATS = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency (€)' },
  { value: 'percent', label: 'Percent (%)' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & Time' },
  { value: 'boolean', label: 'Yes/No' },
];

const PRESET_COLORS = [
  { bg: '#dcfce7', text: '#166534', label: 'Green' },
  { bg: '#fef9c3', text: '#854d0e', label: 'Yellow' },
  { bg: '#fee2e2', text: '#991b1b', label: 'Red' },
  { bg: '#dbeafe', text: '#1e40af', label: 'Blue' },
  { bg: '#f3e8ff', text: '#7c3aed', label: 'Purple' },
  { bg: '#ffedd5', text: '#c2410c', label: 'Orange' },
];

// Variable suggestions for conditional formatting
const VARIABLE_SUGGESTIONS = [
  { category: 'Common', items: [
    { value: 'today()', label: 'Today', description: 'Current date (00:00)' },
    { value: 'now()', label: 'Now', description: 'Current date and time' },
    { value: 'yesterday()', label: 'Yesterday', description: 'Yesterday\'s date' },
    { value: 'tomorrow()', label: 'Tomorrow', description: 'Tomorrow\'s date' },
  ]},
  { category: 'Week', items: [
    { value: 'startOfWeek()', label: 'Start of Week', description: 'Monday of current week' },
    { value: 'endOfWeek()', label: 'End of Week', description: 'Sunday of current week' },
  ]},
  { category: 'Month', items: [
    { value: 'startOfMonth()', label: 'Start of Month', description: 'First day of current month' },
    { value: 'endOfMonth()', label: 'End of Month', description: 'Last day of current month' },
  ]},
  { category: 'Relative', items: [
    { value: 'daysAgo(7)', label: '7 Days Ago', description: 'One week in the past' },
    { value: 'daysAgo(30)', label: '30 Days Ago', description: 'One month in the past' },
    { value: 'daysFromNow(7)', label: 'In 7 Days', description: 'One week in the future' },
    { value: 'daysFromNow(30)', label: 'In 30 Days', description: 'One month in the future' },
  ]},
];

// Known OData Relations for Infor LN Entities (will be loaded from $metadata later)
// Navigation Property names from tdapi.slsSalesOrder.odata-svc.json
// Keys can be: "namespace/EntitySet" for namespace-specific or just "EntitySet" for generic
const KNOWN_ENTITY_RELATIONS: Record<string, Array<{ name: string; targetEntity: string; multiplicity: '1' | '*' }>> = {
  // Sales Orders - tdapi.slsSalesOrder/Orders (namespace-specific)
  'tdapi.slsSalesOrder/Orders': [
    { name: 'LineRefs', targetEntity: 'Line', multiplicity: '*' },
    { name: 'ActualDeliveryLineRefs', targetEntity: 'ActualDeliveryLine', multiplicity: '*' },
    { name: 'ActualReceiptLineRefs', targetEntity: 'ActualReceiptLine', multiplicity: '*' },
    { name: 'WarehouseOrderLineRefs', targetEntity: 'WarehouseOrderLine', multiplicity: '*' },
  ],
  // Production Orders - tiapi.sfcProductionOrder/Orders (namespace-specific)
  'tiapi.sfcProductionOrder/Orders': [
    { name: 'OperationRefs', targetEntity: 'Operation', multiplicity: '*' },
    { name: 'MaterialRefs', targetEntity: 'Material', multiplicity: '*' },
    { name: 'EstimatedMaterialRefs', targetEntity: 'EstimatedMaterial', multiplicity: '*' },
  ],
  // Generic Sales Orders fallback
  'SalesOrders': [
    { name: 'LineRefs', targetEntity: 'Line', multiplicity: '*' },
    { name: 'ActualDeliveryLineRefs', targetEntity: 'ActualDeliveryLine', multiplicity: '*' },
    { name: 'ActualReceiptLineRefs', targetEntity: 'ActualReceiptLine', multiplicity: '*' },
  ],
  // Generic Production Orders fallback
  'ProductionOrders': [
    { name: 'OperationRefs', targetEntity: 'Operation', multiplicity: '*' },
    { name: 'MaterialRefs', targetEntity: 'Material', multiplicity: '*' },
  ],
  // Purchase Orders
  'PurchaseOrders': [
    { name: 'OrderLines', targetEntity: 'PurchaseOrderLine', multiplicity: '*' },
    { name: 'Supplier', targetEntity: 'BusinessPartner', multiplicity: '1' },
  ],
  // Items
  'Items': [
    { name: 'ItemPrices', targetEntity: 'ItemPrice', multiplicity: '*' },
    { name: 'ItemInventory', targetEntity: 'ItemInventory', multiplicity: '*' },
    { name: 'BillOfMaterials', targetEntity: 'BOMLine', multiplicity: '*' },
  ],
  // Invoices
  'Invoices': [
    { name: 'InvoiceLines', targetEntity: 'InvoiceLine', multiplicity: '*' },
    { name: 'Customer', targetEntity: 'BusinessPartner', multiplicity: '1' },
  ],
  // Generic fallback
  'default': [
    { name: 'Details', targetEntity: 'Detail', multiplicity: '*' },
    { name: 'RelatedItems', targetEntity: 'RelatedItem', multiplicity: '*' },
  ],
};

// Get available relations for an endpoint
function getAvailableRelationsForEndpoint(endpoint: any): Array<{ name: string; targetEntity: string; multiplicity: '1' | '*' }> {
  if (!endpoint) return [];

  // Extract entity set and namespace from path
  // Path format: /TENANT/LN/lnapi/odata/{namespace}/{entitySet}
  // e.g., /LN/lnapi/odata/tdapi.slsSalesOrder/Orders
  const pathParts = endpoint.path?.split('/') || [];
  const entitySet = endpoint.odata_entity_set || pathParts[pathParts.length - 1] || '';

  // Try to extract namespace from path (the part before the entity set containing a dot)
  let namespace = '';
  for (let i = pathParts.length - 2; i >= 0; i--) {
    if (pathParts[i] && pathParts[i].includes('.')) {
      namespace = pathParts[i];
      break;
    }
  }

  // Try namespace-specific key first (e.g., "tdapi.slsSalesOrder/Orders")
  const namespaceKey = namespace ? `${namespace}/${entitySet}` : '';
  if (namespaceKey && KNOWN_ENTITY_RELATIONS[namespaceKey]) {
    return KNOWN_ENTITY_RELATIONS[namespaceKey];
  }

  // Fall back to entity set name only
  if (KNOWN_ENTITY_RELATIONS[entitySet]) {
    return KNOWN_ENTITY_RELATIONS[entitySet];
  }

  // Default fallback
  return KNOWN_ENTITY_RELATIONS['default'];
}

// Build OData Query Preview mit $expand
function buildODataQueryPreview(
  entitySet: string,
  selectFields: string[],
  filters: ODataFilter[],
  expandRelations: ExpandRelation[]
): string {
  const builder = new ODataQueryBuilder(entitySet);

  // $select
  if (selectFields.length > 0) {
    builder.select(selectFields);
  }

  // $filter
  if (filters.length > 0) {
    builder.filter(filters);
  }

  // $expand
  const enabledRelations = expandRelations.filter(r => r.enabled);
  if (enabledRelations.length > 0) {
    const expandConfig: ODataExpand[] = enabledRelations.map(r => ({
      name: r.entity,
      select: r.select,
      filter: r.filter && r.filter.length > 0
        ? r.filter.map(f => `${f.field} ${f.operator} '${f.value}'`).join(' and ')
        : undefined,
    }));
    builder.expand(expandConfig);
  }

  return builder.build();
}

export function WidgetEditorModal({ widget, onClose, onSaved }: WidgetEditorModalProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'general' | 'fields' | 'columns' | 'serverFilter' | 'formatting'>('general');
  const [formData, setFormData] = useState<{
    name: string;
    widget_type: WidgetType;
    endpoint_id: string;
    config: WidgetConfig;
  }>({
    name: widget.name,
    widget_type: widget.widget_type,
    endpoint_id: widget.endpoint_id || '',
    config: widget.config || {},
  });

  // Column selection state
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchSelected, setSearchSelected] = useState('');
  const [selectedAvailableFields, setSelectedAvailableFields] = useState<string[]>([]);
  const [selectedColumnFields, setSelectedColumnFields] = useState<string[]>([]);

  // Fetch available endpoints
  const { data: endpoints, isLoading: endpointsLoading } = useQuery({
    queryKey: ['endpoints'],
    queryFn: endpointsApi.list,
  });

  // Fetch endpoint schema to get available fields
  const { data: schemaData, isLoading: schemaLoading, refetch: refetchSchema } = useQuery({
    queryKey: ['schema', formData.endpoint_id],
    queryFn: () => dataApi.getSchema(formData.endpoint_id),
    enabled: !!formData.endpoint_id,
    retry: 1,
  });

  // State for schema discovery
  const [isDiscoveringSchema, setIsDiscoveringSchema] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  // Discover schema by fetching endpoint data
  const discoverSchemaMutation = useMutation({
    mutationFn: async (endpointId: string) => {
      try {
        const schema = await endpointsApi.discover(endpointId);
        return schema;
      } catch {
        await endpointsApi.fetch(endpointId);
        const schema = await endpointsApi.discover(endpointId);
        return schema;
      }
    },
    onSuccess: () => {
      setSchemaError(null);
      setTimeout(() => refetchSchema(), 500);
    },
    onError: (error: any) => {
      setSchemaError(error?.message || 'Failed to discover schema. Make sure the endpoint has data.');
    },
    onSettled: () => {
      setIsDiscoveringSchema(false);
    }
  });

  const handleDiscoverSchema = useCallback(() => {
    if (formData.endpoint_id && !isDiscoveringSchema) {
      setIsDiscoveringSchema(true);
      discoverSchemaMutation.mutate(formData.endpoint_id);
    }
  }, [formData.endpoint_id, isDiscoveringSchema, discoverSchemaMutation]);

  // Extract available fields from schema
  const availableFields = useMemo(() => {
    if (!schemaData?.fields) return [];
    return schemaData.fields.map((f: any) => ({
      name: f.field_path || f.name || f,
      type: f.data_type || 'string',
    }));
  }, [schemaData]);

  // Get selected columns from config
  const selectedColumns = formData.config.columns || [];

  // Get fields that are not yet selected as columns
  const unselectedFields = useMemo(() => {
    const selectedFieldNames = new Set(selectedColumns.map(c => c.field));
    return availableFields.filter((f: any) => !selectedFieldNames.has(f.name));
  }, [availableFields, selectedColumns]);

  // Filter available fields by search
  const filteredAvailableFields = useMemo(() => {
    if (!searchAvailable.trim()) return unselectedFields;
    const search = searchAvailable.toLowerCase();
    return unselectedFields.filter((f: any) =>
      f.name.toLowerCase().includes(search) ||
      f.type.toLowerCase().includes(search)
    );
  }, [unselectedFields, searchAvailable]);

  // Filter selected columns by search
  const filteredSelectedColumns = useMemo(() => {
    if (!searchSelected.trim()) return selectedColumns;
    const search = searchSelected.toLowerCase();
    return selectedColumns.filter(c =>
      c.field.toLowerCase().includes(search) ||
      (c.header || '').toLowerCase().includes(search)
    );
  }, [selectedColumns, searchSelected]);

  // Helper to format field names as readable headers
  function formatFieldName(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/\./g, ' > ')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Helper to infer column format from data type
  function inferColumnFormat(dataType: string): ColumnFormat | undefined {
    const type = dataType.toLowerCase();
    if (type.includes('int') || type.includes('decimal') || type.includes('number') || type.includes('float')) {
      return { type: 'number', thousandsSeparator: true };
    }
    if (type.includes('date') && type.includes('time')) {
      return { type: 'datetime' };
    }
    if (type.includes('date')) {
      return { type: 'date' };
    }
    if (type.includes('bool')) {
      return { type: 'boolean' };
    }
    if (type.includes('currency') || type.includes('money') || type.includes('amount')) {
      return { type: 'currency', decimals: 2 };
    }
    if (type.includes('percent')) {
      return { type: 'percent', decimals: 1 };
    }
    return undefined;
  }

  // Add selected fields to columns
  const addFieldsToColumns = useCallback((fieldNames: string[]) => {
    const fieldsToAdd = availableFields.filter((f: any) => fieldNames.includes(f.name));
    const newColumns: TableColumn[] = fieldsToAdd.map((field: any) => ({
      field: field.name,
      header: formatFieldName(field.name),
      sortable: true,
      filterable: true,
      format: inferColumnFormat(field.type),
    }));

    updateConfig({ columns: [...selectedColumns, ...newColumns] });
    setSelectedAvailableFields([]);
  }, [availableFields, selectedColumns]);

  // Remove columns
  const removeColumns = useCallback((fieldNames: string[]) => {
    const updatedColumns = selectedColumns.filter(c => !fieldNames.includes(c.field));
    updateConfig({ columns: updatedColumns });
    setSelectedColumnFields([]);
  }, [selectedColumns]);

  // Move column up
  const moveColumnUp = useCallback((fieldName: string) => {
    const index = selectedColumns.findIndex(c => c.field === fieldName);
    if (index > 0) {
      const newColumns = [...selectedColumns];
      [newColumns[index - 1], newColumns[index]] = [newColumns[index], newColumns[index - 1]];
      updateConfig({ columns: newColumns });
    }
  }, [selectedColumns]);

  // Move column down
  const moveColumnDown = useCallback((fieldName: string) => {
    const index = selectedColumns.findIndex(c => c.field === fieldName);
    if (index < selectedColumns.length - 1) {
      const newColumns = [...selectedColumns];
      [newColumns[index], newColumns[index + 1]] = [newColumns[index + 1], newColumns[index]];
      updateConfig({ columns: newColumns });
    }
  }, [selectedColumns]);

  // Move column to top
  const moveColumnToTop = useCallback((fieldName: string) => {
    const index = selectedColumns.findIndex(c => c.field === fieldName);
    if (index > 0) {
      const newColumns = [...selectedColumns];
      const [column] = newColumns.splice(index, 1);
      newColumns.unshift(column);
      updateConfig({ columns: newColumns });
    }
  }, [selectedColumns]);

  // Move column to bottom
  const moveColumnToBottom = useCallback((fieldName: string) => {
    const index = selectedColumns.findIndex(c => c.field === fieldName);
    if (index < selectedColumns.length - 1) {
      const newColumns = [...selectedColumns];
      const [column] = newColumns.splice(index, 1);
      newColumns.push(column);
      updateConfig({ columns: newColumns });
    }
  }, [selectedColumns]);

  // Move column to specific position
  const moveColumnToPosition = useCallback((fieldName: string, newPosition: number) => {
    const index = selectedColumns.findIndex(c => c.field === fieldName);
    if (index === -1 || newPosition < 0 || newPosition >= selectedColumns.length) return;

    const newColumns = [...selectedColumns];
    const [column] = newColumns.splice(index, 1);
    newColumns.splice(newPosition, 0, column);
    updateConfig({ columns: newColumns });
  }, [selectedColumns]);

  // Drag and drop handler
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, fieldName: string) => {
    setDraggedColumn(fieldName);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fieldName);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedColumn(null);
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const fieldName = e.dataTransfer.getData('text/plain');
    if (fieldName) {
      moveColumnToPosition(fieldName, targetIndex);
    }
    handleDragEnd();
  }, [moveColumnToPosition, handleDragEnd]);

  // Select all available fields
  const selectAllFields = useCallback(() => {
    const allFieldNames = availableFields.map((f: any) => f.name);
    const newColumns: TableColumn[] = availableFields.map((field: any) => ({
      field: field.name,
      header: formatFieldName(field.name),
      sortable: true,
      filterable: true,
      format: inferColumnFormat(field.type),
    }));
    updateConfig({ columns: newColumns });
  }, [availableFields]);

  // Clear all columns
  const clearAllColumns = useCallback(() => {
    updateConfig({ columns: [] });
  }, []);

  // Reset to default (first 15 fields)
  const resetToDefault = useCallback(() => {
    const defaultColumns: TableColumn[] = availableFields.slice(0, 15).map((field: any) => ({
      field: field.name,
      header: formatFieldName(field.name),
      sortable: true,
      filterable: true,
      format: inferColumnFormat(field.type),
    }));
    updateConfig({ columns: defaultColumns });
  }, [availableFields]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<Widget>) => widgetsApi.update(widget.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
      onSaved();
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      name: formData.name,
      widget_type: formData.widget_type,
      endpoint_id: formData.endpoint_id || null,
      config: formData.config,
    });
  };

  const updateConfig = (updates: Partial<WidgetConfig>) => {
    setFormData(prev => ({
      ...prev,
      config: { ...prev.config, ...updates }
    }));
  };

  const selectedEndpoint = endpoints?.find(e => e.id === formData.endpoint_id);
  const isChartType = ['chart_pie', 'chart_bar', 'chart_line'].includes(formData.widget_type);
  const isTableType = formData.widget_type === 'table';
  const isKPIType = formData.widget_type === 'kpi';

  // Fetch OData schema from imported service definitions
  const {
    navigationProperties: schemaNavigationProperties,
    hasServiceDefinition,
    isLoading: isSchemaLoading,
  } = useODataSchema(
    selectedEndpoint?.connection_id,
    selectedEndpoint?.path || selectedEndpoint?.odata_entity_set
  );

  // Get target entity name for expandable rows (e.g., LineRefs -> Lines)
  const getTargetEntitySetName = (navPropName: string): string => {
    // Try to find the target entity from schema navigation properties
    const navProp = schemaNavigationProperties.find(np => np.name === navPropName);
    if (navProp?.targetEntity) {
      // Target entity is singular (e.g., "Line"), but the entity set is plural (e.g., "Lines")
      return navProp.targetEntity + 's'; // Simple pluralization
    }
    // Fallback: derive from navigation property name
    if (navPropName.endsWith('Refs')) {
      return navPropName.slice(0, -4) + 's'; // LineRefs -> Lines
    }
    return navPropName;
  };

  // Determine the target entity set name for expandable rows
  const expandableDataField = formData.config.expandable?.dataField;
  const targetEntitySetName = expandableDataField ? getTargetEntitySetName(expandableDataField) : '';

  // Fetch properties for the target entity (e.g., Lines) - used for expandable columns dropdown
  const { data: targetEntityProperties, isLoading: isTargetPropertiesLoading } = useQuery({
    queryKey: ['target-entity-properties', selectedEndpoint?.connection_id, targetEntitySetName],
    queryFn: () => odataServicesApi.getEntityProperties(
      selectedEndpoint!.connection_id!,
      targetEntitySetName
    ),
    enabled: !!selectedEndpoint?.connection_id && !!targetEntitySetName && hasServiceDefinition,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });

  // Get available relations - prefer schema from service definition, fall back to hardcoded
  const getRelationsWithSchema = useCallback(() => {
    if (hasServiceDefinition && schemaNavigationProperties.length > 0) {
      console.log('Using schema from service definition:', schemaNavigationProperties);
      return schemaNavigationProperties;
    }
    // Fall back to hardcoded relations
    return selectedEndpoint ? getAvailableRelationsForEndpoint(selectedEndpoint) : [];
  }, [hasServiceDefinition, schemaNavigationProperties, selectedEndpoint]);

  // Cleanup orphaned relations when endpoint changes
  // This removes relations that are no longer in available relations (from schema or hardcoded)
  useEffect(() => {
    if (!selectedEndpoint || !formData.config.expandRelations || isSchemaLoading) return;

    const availableRelations = getRelationsWithSchema();
    const availableRelationNames = new Set(availableRelations.map(r => r.name));

    const currentRelations = formData.config.expandRelations as ExpandRelation[];
    const validRelations = currentRelations.filter(r => availableRelationNames.has(r.entity));

    // Only update if there are orphaned relations to remove
    if (validRelations.length < currentRelations.length) {
      const orphanedRelations = currentRelations.filter(r => !availableRelationNames.has(r.entity)).map(r => r.entity);
      console.log('Cleaning up orphaned relations:', orphanedRelations);

      // Also clear expandable.dataField if it references an orphaned relation
      const currentDataField = formData.config.expandable?.dataField;
      const isDataFieldOrphaned = currentDataField && orphanedRelations.includes(currentDataField);

      setFormData(prev => ({
        ...prev,
        config: {
          ...prev.config,
          expandRelations: validRelations,
          // Clear expandable config if dataField references orphaned relation
          ...(isDataFieldOrphaned && {
            expandable: {
              ...prev.config.expandable,
              enabled: false,
              dataField: '',
              columns: prev.config.expandable?.columns || [],
            }
          }),
        }
      }));
    }
  }, [selectedEndpoint, isSchemaLoading, getRelationsWithSchema]); // eslint-disable-line react-hooks/exhaustive-deps

  // Conditional formatting helpers
  const addConditionalFormat = (columnField: string) => {
    const columns = formData.config.columns || [];
    const colIndex = columns.findIndex(c => c.field === columnField);
    if (colIndex === -1) return;

    const newFormat: ConditionalFormat = {
      id: `cf-${Date.now()}`,
      condition: { operator: 'equals', value: '' },
      style: { backgroundColor: '#dcfce7', textColor: '#166534' }
    };

    const updatedColumns = [...columns];
    updatedColumns[colIndex] = {
      ...updatedColumns[colIndex],
      conditionalFormats: [...(updatedColumns[colIndex].conditionalFormats || []), newFormat]
    };

    updateConfig({ columns: updatedColumns });
  };

  const removeConditionalFormat = (columnField: string, formatId: string) => {
    const columns = formData.config.columns || [];
    const colIndex = columns.findIndex(c => c.field === columnField);
    if (colIndex === -1) return;

    const updatedColumns = [...columns];
    updatedColumns[colIndex] = {
      ...updatedColumns[colIndex],
      conditionalFormats: updatedColumns[colIndex].conditionalFormats?.filter(cf => cf.id !== formatId)
    };

    updateConfig({ columns: updatedColumns });
  };

  const updateConditionalFormat = (columnField: string, formatId: string, updates: Partial<ConditionalFormat>) => {
    const columns = formData.config.columns || [];
    const colIndex = columns.findIndex(c => c.field === columnField);
    if (colIndex === -1) return;

    const updatedColumns = [...columns];
    updatedColumns[colIndex] = {
      ...updatedColumns[colIndex],
      conditionalFormats: updatedColumns[colIndex].conditionalFormats?.map(cf =>
        cf.id === formatId ? { ...cf, ...updates } : cf
      )
    };

    updateConfig({ columns: updatedColumns });
  };

  const updateColumnFormat = (columnField: string, format: Partial<ColumnFormat>) => {
    const columns = formData.config.columns || [];
    const colIndex = columns.findIndex(c => c.field === columnField);
    if (colIndex === -1) return;

    const updatedColumns = [...columns];
    updatedColumns[colIndex] = {
      ...updatedColumns[colIndex],
      format: { ...updatedColumns[colIndex].format, ...format } as ColumnFormat
    };

    updateConfig({ columns: updatedColumns });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit Widget</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'general'
                ? 'border-[#45bdd6] text-[#45bdd6]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            General
          </button>
          {(isChartType || isKPIType) && formData.endpoint_id && (
            <button
              onClick={() => setActiveTab('fields')}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
                activeTab === 'fields'
                  ? 'border-[#45bdd6] text-[#45bdd6]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Database className="w-4 h-4 inline mr-2" />
              Field Selection
            </button>
          )}
          {isTableType && formData.endpoint_id && (
            <button
              onClick={() => setActiveTab('columns')}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
                activeTab === 'columns'
                  ? 'border-[#45bdd6] text-[#45bdd6]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Columns className="w-4 h-4 inline mr-2" />
              Column Selection
            </button>
          )}
          {isTableType && formData.endpoint_id && (
            <button
              onClick={() => setActiveTab('serverFilter')}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
                activeTab === 'serverFilter'
                  ? 'border-[#45bdd6] text-[#45bdd6]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Filter className="w-4 h-4 inline mr-2" />
              Server Filter
            </button>
          )}
          {isTableType && formData.endpoint_id && selectedColumns.length > 0 && (
            <button
              onClick={() => setActiveTab('formatting')}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
                activeTab === 'formatting'
                  ? 'border-[#45bdd6] text-[#45bdd6]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Palette className="w-4 h-4 inline mr-2" />
              Formatting
            </button>
          )}
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-auto p-6">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              {/* Widget Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Widget Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
                  required
                />
              </div>

              {/* Widget Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Widget Type
                </label>
                <select
                  value={formData.widget_type}
                  onChange={(e) => setFormData({ ...formData, widget_type: e.target.value as WidgetType })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
                >
                  {WIDGET_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Endpoint Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Source (Endpoint)
                </label>
                {endpointsLoading ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Loading endpoints...
                  </div>
                ) : (
                  <select
                    value={formData.endpoint_id}
                    onChange={(e) => setFormData({ ...formData, endpoint_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
                  >
                    <option value="">-- No Endpoint --</option>
                    {endpoints?.map((endpoint) => (
                      <option key={endpoint.id} value={endpoint.id}>
                        {endpoint.name} ({endpoint.path})
                      </option>
                    ))}
                  </select>
                )}
                {selectedEndpoint && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 text-blue-700">
                      <Database className="w-4 h-4" />
                      <span className="font-medium">{selectedEndpoint.name}</span>
                    </div>
                    <p className="text-sm text-blue-600 mt-1">{selectedEndpoint.path}</p>
                    {selectedEndpoint.last_fetched_at && (
                      <p className="text-xs text-blue-500 mt-1">
                        Last fetched: {new Date(selectedEndpoint.last_fetched_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Table-specific options */}
              {isTableType && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Table Options</h3>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.config.showPagination !== false}
                        onChange={(e) => updateConfig({ showPagination: e.target.checked })}
                        className="rounded border-gray-300 text-[#45bdd6]"
                      />
                      <span className="text-sm text-gray-600">Show pagination</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.config.striped === true}
                        onChange={(e) => updateConfig({ striped: e.target.checked })}
                        className="rounded border-gray-300 text-[#45bdd6]"
                      />
                      <span className="text-sm text-gray-600">Striped rows</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.config.showColumnFilters === true}
                        onChange={(e) => updateConfig({ showColumnFilters: e.target.checked })}
                        className="rounded border-gray-300 text-[#45bdd6]"
                      />
                      <span className="text-sm text-gray-600">Column filters (Incremental Search)</span>
                    </label>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Page Size</label>
                      <input
                        type="number"
                        value={formData.config.pageSize || 20}
                        onChange={(e) => updateConfig({ pageSize: parseInt(e.target.value) || 20 })}
                        className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded"
                        min={5}
                        max={100}
                      />
                    </div>
                  </div>

                  {/* Expandable Rows Section - for $expand data */}
                  {formData.config.expandRelations && formData.config.expandRelations.filter((r: ExpandRelation) => r.enabled && r.multiplicity === '*').length > 0 && (
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <ChevronRight className="w-4 h-4" />
                        Expandable Rows (Nested Data)
                      </h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Display related data like OrderLines in expandable rows.
                      </p>

                      <div className="space-y-3">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.config.expandable?.enabled === true}
                            onChange={(e) => updateConfig({
                              expandable: {
                                ...formData.config.expandable,
                                enabled: e.target.checked,
                                dataField: formData.config.expandable?.dataField || '',
                                columns: formData.config.expandable?.columns || [],
                              }
                            })}
                            className="rounded border-gray-300 text-[#45bdd6]"
                          />
                          <span className="text-sm text-gray-600">Enable Expandable Rows</span>
                        </label>

                        {formData.config.expandable?.enabled && (
                          <>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Nested Data Field</label>
                              <select
                                value={formData.config.expandable?.dataField || ''}
                                onChange={(e) => {
                                  const selectedField = e.target.value;
                                  const selectedRelation = getRelationsWithSchema().find(r => r.name === selectedField);

                                  // Update expandable config
                                  const updatedConfig: Record<string, unknown> = {
                                    expandable: {
                                      ...formData.config.expandable!,
                                      dataField: selectedField,
                                    }
                                  };

                                  // Also ensure the relation is in expandRelations for the backend $expand
                                  if (selectedField && selectedRelation) {
                                    const currentRelations = (formData.config.expandRelations as ExpandRelation[]) || [];
                                    const existingRelation = currentRelations.find(r => r.entity === selectedField);

                                    if (!existingRelation) {
                                      // Add the relation to expandRelations
                                      updatedConfig.expandRelations = [
                                        ...currentRelations,
                                        {
                                          entity: selectedField,
                                          targetEntity: selectedRelation.targetEntity,
                                          multiplicity: selectedRelation.multiplicity,
                                          enabled: true,  // Enable it for $expand
                                        }
                                      ];
                                    } else if (!existingRelation.enabled) {
                                      // Enable the existing relation
                                      updatedConfig.expandRelations = currentRelations.map(r =>
                                        r.entity === selectedField ? { ...r, enabled: true } : r
                                      );
                                    }
                                  }

                                  updateConfig(updatedConfig);
                                }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                              >
                                <option value="">-- Select Field --</option>
                                {/* Use getRelationsWithSchema() which includes both imported schema and fallback relations */}
                                {getRelationsWithSchema()
                                  .filter((r) => r.multiplicity === '*')
                                  .map((r) => (
                                    <option key={r.name} value={r.name}>
                                      {r.name} ({r.targetEntity || 'Details'})
                                    </option>
                                  ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Title for Nested Data</label>
                              <input
                                type="text"
                                value={formData.config.expandable?.title || ''}
                                onChange={(e) => updateConfig({
                                  expandable: {
                                    ...formData.config.expandable!,
                                    title: e.target.value,
                                  }
                                })}
                                placeholder="e.g. Order Lines"
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                              />
                            </div>

                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={formData.config.expandable?.showCount === true}
                                onChange={(e) => updateConfig({
                                  expandable: {
                                    ...formData.config.expandable!,
                                    showCount: e.target.checked,
                                  }
                                })}
                                className="rounded border-gray-300 text-[#45bdd6]"
                              />
                              <span className="text-sm text-gray-600">Show count badge</span>
                            </label>

                            {/* Nested Columns Configuration */}
                            {formData.config.expandable?.dataField && (
                              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-2">
                                  Columns for "{formData.config.expandable.dataField}"
                                  {targetEntityProperties?.found && (
                                    <span className="text-xs text-green-600 bg-green-100 px-1.5 py-0.5 rounded">
                                      Schema loaded
                                    </span>
                                  )}
                                  {isTargetPropertiesLoading && (
                                    <span className="text-xs text-blue-500">Loading fields...</span>
                                  )}
                                </h5>
                                <div className="space-y-1">
                                  {(formData.config.expandable?.columns || []).map((col, idx) => (
                                    <div key={idx} className="flex items-center gap-2 text-sm">
                                      {/* Field selector - dropdown if properties available, text input otherwise */}
                                      {targetEntityProperties?.found && targetEntityProperties.properties.length > 0 ? (
                                        <select
                                          value={col.field}
                                          onChange={(e) => {
                                            const newCols = [...(formData.config.expandable?.columns || [])];
                                            const selectedProp = targetEntityProperties.properties.find(p => p.name === e.target.value);
                                            newCols[idx] = {
                                              ...newCols[idx],
                                              field: e.target.value,
                                              // Auto-fill header if empty
                                              header: newCols[idx].header || e.target.value
                                            };
                                            updateConfig({
                                              expandable: { ...formData.config.expandable!, columns: newCols }
                                            });
                                          }}
                                          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                                        >
                                          <option value="">-- Select Field --</option>
                                          {targetEntityProperties.properties.map(prop => (
                                            <option key={prop.name} value={prop.name}>
                                              {prop.name} ({prop.type})
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          value={col.field}
                                          onChange={(e) => {
                                            const newCols = [...(formData.config.expandable?.columns || [])];
                                            newCols[idx] = { ...newCols[idx], field: e.target.value };
                                            updateConfig({
                                              expandable: { ...formData.config.expandable!, columns: newCols }
                                            });
                                          }}
                                          placeholder="Field name (e.g. Line, Item, OrderedQuantity)"
                                          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                                        />
                                      )}
                                      <input
                                        type="text"
                                        value={col.header}
                                        onChange={(e) => {
                                          const newCols = [...(formData.config.expandable?.columns || [])];
                                          newCols[idx] = { ...newCols[idx], header: e.target.value };
                                          updateConfig({
                                            expandable: { ...formData.config.expandable!, columns: newCols }
                                          });
                                        }}
                                        placeholder="Header"
                                        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newCols = (formData.config.expandable?.columns || []).filter((_, i) => i !== idx);
                                          updateConfig({
                                            expandable: { ...formData.config.expandable!, columns: newCols }
                                          });
                                        }}
                                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newCols = [...(formData.config.expandable?.columns || []), { field: '', header: '' }];
                                      updateConfig({
                                        expandable: { ...formData.config.expandable!, columns: newCols }
                                      });
                                    }}
                                    className="flex items-center gap-1 px-2 py-1 text-xs text-[#45bdd6] hover:bg-blue-50 rounded mt-2"
                                  >
                                    <Plus className="w-3 h-3" />
                                    Add Column
                                  </button>
                                </div>
                                {/* Quick add common fields */}
                                {targetEntityProperties?.found && targetEntityProperties.properties.length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                    <p className="text-xs text-gray-500 mb-1">Quick add recommended fields:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {['Line', 'Item', 'OrderedQuantity', 'Amount', 'Price', 'SalesUnit'].map(fieldName => {
                                        const prop = targetEntityProperties.properties.find(p => p.name === fieldName);
                                        if (!prop) return null;
                                        const alreadyAdded = (formData.config.expandable?.columns || []).some(c => c.field === fieldName);
                                        return (
                                          <button
                                            key={fieldName}
                                            type="button"
                                            disabled={alreadyAdded}
                                            onClick={() => {
                                              const newCols = [...(formData.config.expandable?.columns || []), { field: fieldName, header: fieldName }];
                                              updateConfig({
                                                expandable: { ...formData.config.expandable!, columns: newCols }
                                              });
                                            }}
                                            className={`px-2 py-0.5 text-xs rounded ${
                                              alreadyAdded
                                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                            }`}
                                          >
                                            + {fieldName}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* KPI-specific options */}
              {isKPIType && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">KPI Options</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Aggregation</label>
                      <select
                        value={formData.config.aggregation || 'count'}
                        onChange={(e) => updateConfig({ aggregation: e.target.value as any })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      >
                        {AGGREGATIONS.map(agg => (
                          <option key={agg.value} value={agg.value}>{agg.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Label</label>
                      <input
                        type="text"
                        value={formData.config.label || ''}
                        onChange={(e) => updateConfig({ label: e.target.value })}
                        placeholder="e.g. Total Orders"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  </div>

                  {/* KPI Filter: Count only records matching field = value */}
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <h4 className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1.5">
                      <Filter className="w-3.5 h-3.5" />
                      Filter (count only matching records)
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Filter Field</label>
                        <select
                          value={
                            (formData.config.serverFilters && formData.config.serverFilters.length > 0)
                              ? formData.config.serverFilters[0].field
                              : ''
                          }
                          onChange={(e) => {
                            const fieldName = e.target.value;
                            if (!fieldName) {
                              // Remove filter
                              updateConfig({ serverFilters: [] });
                            } else {
                              const existingFilters = formData.config.serverFilters || [];
                              const existingValue = existingFilters.length > 0 ? existingFilters[0].value : '';
                              updateConfig({
                                serverFilters: [{
                                  field: fieldName,
                                  operator: 'eq' as const,
                                  value: existingValue || '',
                                }]
                              });
                            }
                          }}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                        >
                          <option value="">-- No Filter (count all) --</option>
                          {availableFields.map((field: any) => (
                            <option key={field.name} value={field.name}>
                              {field.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Filter Value</label>
                        <input
                          type="text"
                          value={
                            (formData.config.serverFilters && formData.config.serverFilters.length > 0)
                              ? String(formData.config.serverFilters[0].value || '')
                              : ''
                          }
                          onChange={(e) => {
                            const existingFilters = formData.config.serverFilters || [];
                            const existingField = existingFilters.length > 0 ? existingFilters[0].field : '';
                            if (existingField) {
                              updateConfig({
                                serverFilters: [{
                                  field: existingField,
                                  operator: 'eq' as const,
                                  value: e.target.value,
                                }]
                              });
                            }
                          }}
                          disabled={!formData.config.serverFilters || formData.config.serverFilters.length === 0}
                          placeholder="e.g. Active"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded disabled:bg-gray-50 disabled:text-gray-400"
                        />
                      </div>
                    </div>
                    {formData.config.serverFilters && formData.config.serverFilters.length > 0 && formData.config.serverFilters[0].field && formData.config.serverFilters[0].value && (
                      <p className="text-xs text-blue-600 mt-1.5 bg-blue-50 px-2 py-1 rounded">
                        Counting records where <strong>{formData.config.serverFilters[0].field}</strong> = <strong>{String(formData.config.serverFilters[0].value)}</strong>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Chart-specific options */}
              {isChartType && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Chart Options</h3>
                  <div className="space-y-3">
                    {formData.widget_type === 'chart_pie' && (
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.config.donut === true}
                          onChange={(e) => updateConfig({ donut: e.target.checked })}
                          className="rounded border-gray-300 text-[#45bdd6]"
                        />
                        <span className="text-sm text-gray-600">Donut style</span>
                      </label>
                    )}
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.config.showLegend !== false}
                        onChange={(e) => updateConfig({ showLegend: e.target.checked })}
                        className="rounded border-gray-300 text-[#45bdd6]"
                      />
                      <span className="text-sm text-gray-600">Show legend</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.config.showLabels === true}
                        onChange={(e) => updateConfig({ showLabels: e.target.checked })}
                        className="rounded border-gray-300 text-[#45bdd6]"
                      />
                      <span className="text-sm text-gray-600">Show labels</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Fields Tab (for Charts and KPI) */}
          {activeTab === 'fields' && (isChartType || isKPIType) && (
            <div className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-700">
                  Select which fields from your data source to use for the visualization.
                </p>
              </div>

              {schemaLoading ? (
                <div className="text-center py-8 text-gray-500">
                  <RefreshCw className="w-12 h-12 mx-auto mb-2 animate-spin opacity-50" />
                  <p>Loading available fields...</p>
                </div>
              ) : availableFields.length === 0 ? (
                <div className="text-center py-8">
                  <Database className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-500 mb-4">No fields available yet.</p>

                  {schemaError && (
                    <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm">
                      <AlertCircle className="w-4 h-4 inline mr-2" />
                      {schemaError}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleDiscoverSchema}
                    disabled={isDiscoveringSchema}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] disabled:opacity-50"
                  >
                    {isDiscoveringSchema ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Discovering Schema...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4" />
                        Discover Fields from Data
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-400 mt-2">
                    This will fetch data from the endpoint to discover available fields.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Category/Group Field (for charts) */}
                  {isChartType && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {formData.widget_type === 'chart_pie' ? 'Group By (Slices)' : 'Category Field (X-Axis)'}
                      </label>
                      <select
                        value={formData.config.categoryField || ''}
                        onChange={(e) => updateConfig({ categoryField: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6]"
                      >
                        <option value="">-- Select Field --</option>
                        {availableFields.map((field: any) => (
                          <option key={field.name} value={field.name}>
                            {field.name} ({field.type})
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.widget_type === 'chart_pie'
                          ? 'Field used to create pie slices (e.g., Status, Category)'
                          : 'Field shown on the X-axis'
                        }
                      </p>
                    </div>
                  )}

                  {/* Value Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isKPIType ? 'Value Field' : 'Value Field (Y-Axis)'}
                    </label>
                    <select
                      value={formData.config.valueField || formData.config.field || ''}
                      onChange={(e) => updateConfig({
                        valueField: e.target.value,
                        field: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6]"
                    >
                      <option value="">-- Count Records --</option>
                      {availableFields.map((field: any) => (
                        <option key={field.name} value={field.name}>
                          {field.name} ({field.type})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Numeric field to aggregate. Leave empty to count records.
                    </p>
                  </div>

                  {/* Aggregation for charts */}
                  {isChartType && formData.config.valueField && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Aggregation
                      </label>
                      <select
                        value={formData.config.aggregation || 'sum'}
                        onChange={(e) => updateConfig({ aggregation: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6]"
                      >
                        {AGGREGATIONS.map(agg => (
                          <option key={agg.value} value={agg.value}>{agg.label}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Line chart specific */}
                  {formData.widget_type === 'chart_line' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Series Field (optional)
                      </label>
                      <select
                        value={formData.config.seriesField || ''}
                        onChange={(e) => updateConfig({ seriesField: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6]"
                      >
                        <option value="">-- No Series --</option>
                        {availableFields.map((field: any) => (
                          <option key={field.name} value={field.name}>
                            {field.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Field to create multiple lines (e.g., by Status or Category)
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Column Selection Tab (for Tables) - Infor LN Style */}
          {activeTab === 'columns' && isTableType && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Column Personalization:</strong> Select and arrange columns to display in the table. Similar to Infor LN session personalization.
                </p>
              </div>

              {schemaLoading ? (
                <div className="text-center py-8 text-gray-500">
                  <RefreshCw className="w-12 h-12 mx-auto mb-2 animate-spin opacity-50" />
                  <p>Loading available fields...</p>
                </div>
              ) : availableFields.length === 0 ? (
                <div className="text-center py-8">
                  <Database className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-500 mb-4">No fields available yet.</p>

                  {schemaError && (
                    <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm inline-block">
                      <AlertCircle className="w-4 h-4 inline mr-2" />
                      {schemaError}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleDiscoverSchema}
                    disabled={isDiscoveringSchema}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] disabled:opacity-50"
                  >
                    {isDiscoveringSchema ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Discovering Fields...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4" />
                        Discover Fields from Data
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-400 mt-2">
                    This will fetch data from the endpoint to discover available fields.
                  </p>
                </div>
              ) : (
                <>
                  {/* Quick Actions */}
                  <div className="flex items-center justify-between gap-4 pb-4 border-b">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={selectAllFields}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-100 text-green-700 hover:bg-green-200 rounded"
                      >
                        <ChevronsRight className="w-3 h-3" />
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={clearAllColumns}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded"
                      >
                        <ChevronsLeft className="w-3 h-3" />
                        Clear All
                      </button>
                      <button
                        type="button"
                        onClick={resetToDefault}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 rounded"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Reset Default
                      </button>
                    </div>
                    <div className="text-sm text-gray-500">
                      {selectedColumns.length} of {availableFields.length} columns selected
                    </div>
                  </div>

                  {/* Two-Column Layout */}
                  <div className="grid grid-cols-2 gap-4" style={{ minHeight: '400px' }}>
                    {/* Available Fields */}
                    <div className="border rounded-lg flex flex-col">
                      <div className="p-3 bg-gray-50 border-b">
                        <h4 className="font-medium text-gray-700 text-sm mb-2">
                          Available Fields ({unselectedFields.length})
                        </h4>
                        <div className="relative">
                          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={searchAvailable}
                            onChange={(e) => setSearchAvailable(e.target.value)}
                            placeholder="Search fields..."
                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded"
                          />
                        </div>
                      </div>
                      <div className="flex-1 overflow-auto p-2">
                        {filteredAvailableFields.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-4">
                            {searchAvailable ? 'No matching fields' : 'All fields selected'}
                          </p>
                        ) : (
                          <div className="space-y-1">
                            {filteredAvailableFields.map((field: any) => (
                              <div
                                key={field.name}
                                onClick={() => {
                                  setSelectedAvailableFields(prev =>
                                    prev.includes(field.name)
                                      ? prev.filter(f => f !== field.name)
                                      : [...prev, field.name]
                                  );
                                }}
                                className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm ${
                                  selectedAvailableFields.includes(field.name)
                                    ? 'bg-blue-100 border border-blue-300'
                                    : 'hover:bg-gray-100 border border-transparent'
                                }`}
                              >
                                {selectedAvailableFields.includes(field.name) ? (
                                  <CheckSquare className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                ) : (
                                  <Square className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                )}
                                <span className="flex-1 truncate">{field.name}</span>
                                <span className="text-xs text-gray-400 flex-shrink-0">{field.type}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {selectedAvailableFields.length > 0 && (
                        <div className="p-2 border-t bg-gray-50">
                          <button
                            type="button"
                            onClick={() => addFieldsToColumns(selectedAvailableFields)}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#45bdd6] text-white rounded hover:bg-[#3aa8bf] text-sm"
                          >
                            <ChevronRight className="w-4 h-4" />
                            Add {selectedAvailableFields.length} Field(s)
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Selected Columns */}
                    <div className="border rounded-lg flex flex-col">
                      <div className="p-3 bg-gray-50 border-b">
                        <h4 className="font-medium text-gray-700 text-sm mb-2">
                          Selected Columns ({selectedColumns.length})
                        </h4>
                        <div className="relative">
                          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={searchSelected}
                            onChange={(e) => setSearchSelected(e.target.value)}
                            placeholder="Search columns..."
                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded"
                          />
                        </div>
                      </div>
                      <div className="flex-1 overflow-auto p-2">
                        {filteredSelectedColumns.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-4">
                            {searchSelected ? 'No matching columns' : 'No columns selected yet'}
                          </p>
                        ) : (
                          <div className="space-y-1">
                            {filteredSelectedColumns.map((column, index) => {
                              const actualIndex = selectedColumns.findIndex(c => c.field === column.field);
                              return (
                                <div
                                  key={column.field}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, column.field)}
                                  onDragOver={(e) => handleDragOver(e, actualIndex)}
                                  onDragEnd={handleDragEnd}
                                  onDrop={(e) => handleDrop(e, actualIndex)}
                                  className={`flex items-center gap-2 p-2 rounded text-sm transition-all ${
                                    draggedColumn === column.field
                                      ? 'opacity-50 bg-blue-50'
                                      : dragOverIndex === actualIndex
                                      ? 'border-t-2 border-t-blue-500'
                                      : selectedColumnFields.includes(column.field)
                                      ? 'bg-blue-100 border border-blue-300'
                                      : 'hover:bg-gray-100 border border-transparent'
                                  }`}
                                >
                                  <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0 cursor-grab active:cursor-grabbing" />
                                  <div
                                    className="flex-1 cursor-pointer min-w-0"
                                    onClick={() => {
                                      setSelectedColumnFields(prev =>
                                        prev.includes(column.field)
                                          ? prev.filter(f => f !== column.field)
                                          : [...prev, column.field]
                                      );
                                    }}
                                  >
                                    <div className="truncate font-medium">{column.header || column.field}</div>
                                    <div className="text-xs text-gray-400">{column.field}</div>
                                  </div>
                                  <div className="flex items-center gap-0.5 flex-shrink-0">
                                    {/* Move to Top */}
                                    <button
                                      type="button"
                                      onClick={() => moveColumnToTop(column.field)}
                                      disabled={actualIndex === 0}
                                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30 disabled:hover:bg-transparent"
                                      title="Move to top"
                                    >
                                      <ChevronsUp className="w-4 h-4" />
                                    </button>
                                    {/* Move Up */}
                                    <button
                                      type="button"
                                      onClick={() => moveColumnUp(column.field)}
                                      disabled={actualIndex === 0}
                                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded disabled:opacity-30"
                                      title="Move up"
                                    >
                                      <ChevronUp className="w-4 h-4" />
                                    </button>
                                    {/* Move Down */}
                                    <button
                                      type="button"
                                      onClick={() => moveColumnDown(column.field)}
                                      disabled={actualIndex === selectedColumns.length - 1}
                                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded disabled:opacity-30"
                                      title="Move down"
                                    >
                                      <ChevronDown className="w-4 h-4" />
                                    </button>
                                    {/* Move to Bottom */}
                                    <button
                                      type="button"
                                      onClick={() => moveColumnToBottom(column.field)}
                                      disabled={actualIndex === selectedColumns.length - 1}
                                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30 disabled:hover:bg-transparent"
                                      title="Move to bottom"
                                    >
                                      <ChevronsDown className="w-4 h-4" />
                                    </button>
                                    {/* Remove */}
                                    <button
                                      type="button"
                                      onClick={() => removeColumns([column.field])}
                                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded ml-1"
                                      title="Remove column"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {selectedColumnFields.length > 0 && (
                        <div className="p-2 border-t bg-gray-50">
                          <button
                            type="button"
                            onClick={() => removeColumns(selectedColumnFields)}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                          >
                            <ChevronLeft className="w-4 h-4" />
                            Remove {selectedColumnFields.length} Column(s)
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Server Filter Tab (OData $filter + $expand) */}
          {activeTab === 'serverFilter' && isTableType && (
            <div className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Server-side Filters:</strong> These filters are sent directly to the Infor LN API ($filter).
                  This is more efficient than client-side filtering for large data sets.
                </p>
              </div>

              <ServerFilterPanel
                filters={(formData.config.serverFilters as ODataFilter[]) || []}
                availableFields={availableFields.map((f: { name: string; type: string }) => ({
                  name: f.name,
                  dataType: f.type,
                }))}
                onChange={(filters) => updateConfig({ serverFilters: filters })}
                entityName={selectedEndpoint?.odata_entity_set || selectedEndpoint?.name}
              />

              {/* Divider */}
              <div className="border-t border-gray-200 pt-6">
                <div className="bg-purple-50 p-4 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-purple-700">
                    <Link2 className="w-5 h-5" />
                    <strong>Related Data ($expand):</strong>
                  </div>
                  <p className="text-sm text-purple-600 mt-1">
                    Load data from related LN tables in a single query (e.g. Order Lines for Orders).
                  </p>
                </div>

                {/* Relations Panel */}
                {hasServiceDefinition && (
                  <div className="mb-2 text-xs text-green-600 flex items-center gap-1">
                    <CheckSquare className="w-3 h-3" />
                    Navigation Properties loaded from imported Service Definition
                  </div>
                )}
                {!hasServiceDefinition && !isSchemaLoading && (
                  <div className="mb-2 text-xs text-amber-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    No Service Definition found - Fallback to static configuration
                  </div>
                )}
                <RelationsPanel
                  relations={(formData.config.expandRelations as ExpandRelation[]) || []}
                  availableRelations={getRelationsWithSchema()}
                  availableFields={availableFields.map((f: { name: string; type: string }) => ({
                    name: f.name,
                    dataType: f.type,
                  }))}
                  onChange={(relations) => updateConfig({ expandRelations: relations })}
                  entityName={selectedEndpoint?.odata_entity_set || selectedEndpoint?.name}
                />
              </div>

              {/* Combined Query Preview */}
              {(((formData.config.serverFilters as ODataFilter[]) || []).length > 0 ||
                ((formData.config.expandRelations as ExpandRelation[]) || []).filter(r => r.enabled).length > 0) && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Code className="w-5 h-5 text-gray-500" />
                    <h4 className="font-medium text-gray-700">Generierte OData Query</h4>
                  </div>
                  <div className="p-3 bg-gray-900 rounded-lg overflow-x-auto">
                    <code className="text-sm text-green-400 font-mono break-all whitespace-pre-wrap">
                      {buildODataQueryPreview(
                        selectedEndpoint?.odata_entity_set || 'Entity',
                        selectedColumns.map(c => c.field),
                        (formData.config.serverFilters as ODataFilter[]) || [],
                        (formData.config.expandRelations as ExpandRelation[]) || []
                      )}
                    </code>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Formatting Tab (for Tables) */}
          {activeTab === 'formatting' && isTableType && (
            <div className="space-y-6">
              <div className="bg-amber-50 p-4 rounded-lg">
                <p className="text-sm text-amber-700">
                  <strong>Excel-style Formatting:</strong> Configure column formats and conditional formatting rules for the selected columns.
                </p>
              </div>

              {selectedColumns.length === 0 ? (
                <div className="text-center py-8">
                  <Columns className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-500 mb-4">No columns selected yet.</p>
                  <button
                    type="button"
                    onClick={() => setActiveTab('columns')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf]"
                  >
                    <Columns className="w-4 h-4" />
                    Go to Column Selection
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {selectedColumns.map((column) => (
                    <div key={column.field} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{column.header || column.field}</h4>
                        <span className="text-xs text-gray-500">{column.field}</span>
                      </div>

                      {/* Column Format */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Format Type</label>
                          <select
                            value={column.format?.type || 'text'}
                            onChange={(e) => updateColumnFormat(column.field, { type: e.target.value as any })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                          >
                            {COLUMN_FORMATS.map(fmt => (
                              <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
                            ))}
                          </select>
                        </div>
                        {['number', 'currency', 'percent'].includes(column.format?.type || '') && (
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Decimals</label>
                            <input
                              type="number"
                              value={column.format?.decimals ?? 2}
                              onChange={(e) => updateColumnFormat(column.field, { decimals: parseInt(e.target.value) })}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                              min={0}
                              max={10}
                            />
                          </div>
                        )}
                        {column.format?.type === 'currency' && (
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Prefix</label>
                            <input
                              type="text"
                              value={column.format?.prefix ?? '€'}
                              onChange={(e) => updateColumnFormat(column.field, { prefix: e.target.value })}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                              placeholder="€"
                            />
                          </div>
                        )}
                      </div>

                      {/* Conditional Formats */}
                      <div className="border-t pt-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Conditional Formatting</span>
                          <button
                            type="button"
                            onClick={() => addConditionalFormat(column.field)}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-[#45bdd6] hover:bg-blue-50 rounded"
                          >
                            <Plus className="w-3 h-3" />
                            Add Rule
                          </button>
                        </div>

                        {(column.conditionalFormats || []).map((cf) => (
                          <div key={cf.id} className="bg-gray-50 rounded p-3 mb-2">
                            <div className="flex items-center gap-2 mb-2">
                              <select
                                value={cf.condition.operator}
                                onChange={(e) => updateConditionalFormat(column.field, cf.id, {
                                  condition: { ...cf.condition, operator: e.target.value as any }
                                })}
                                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded"
                              >
                                {CONDITION_OPERATORS.map(op => (
                                  <option key={op.value} value={op.value}>{op.label}</option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => removeConditionalFormat(column.field, cf.id)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>

                            {!['is_empty', 'is_not_empty'].includes(cf.condition.operator) && (
                              <div className="space-y-2 mb-2">
                                {/* Value Input with Variable Support */}
                                <div className="relative">
                                  <input
                                    type="text"
                                    value={cf.condition.value ?? ''}
                                    onChange={(e) => updateConditionalFormat(column.field, cf.id, {
                                      condition: { ...cf.condition, value: e.target.value }
                                    })}
                                    placeholder="Value or variable like today()"
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded pr-8"
                                  />
                                  {/* Variable indicator */}
                                  {typeof cf.condition.value === 'string' && cf.condition.value.match(/^[a-zA-Z]+\([^)]*\)$/) && (
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2" title="Dynamic variable">
                                      <Calendar className="w-4 h-4 text-blue-500" />
                                    </div>
                                  )}
                                </div>

                                {/* Variable Quick Select */}
                                <div className="bg-gray-100 rounded p-2">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Clock className="w-4 h-4 text-gray-500" />
                                    <span className="text-xs font-medium text-gray-600">Quick Insert Variable:</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {VARIABLE_SUGGESTIONS.map(category => (
                                      category.items.slice(0, category.category === 'Common' ? 4 : 2).map(variable => (
                                        <button
                                          key={variable.value}
                                          type="button"
                                          onClick={() => updateConditionalFormat(column.field, cf.id, {
                                            condition: { ...cf.condition, value: variable.value }
                                          })}
                                          className={`px-2 py-1 text-xs rounded transition-colors ${
                                            cf.condition.value === variable.value
                                              ? 'bg-blue-500 text-white'
                                              : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                                          }`}
                                          title={variable.description}
                                        >
                                          {variable.label}
                                        </button>
                                      ))
                                    ))}
                                  </div>
                                  <details className="mt-2">
                                    <summary className="text-xs text-blue-600 cursor-pointer hover:underline">
                                      More variables...
                                    </summary>
                                    <div className="mt-2 space-y-2">
                                      {VARIABLE_SUGGESTIONS.map(category => (
                                        <div key={category.category}>
                                          <div className="text-xs font-medium text-gray-500 mb-1">{category.category}</div>
                                          <div className="flex flex-wrap gap-1">
                                            {category.items.map(variable => (
                                              <button
                                                key={variable.value}
                                                type="button"
                                                onClick={() => updateConditionalFormat(column.field, cf.id, {
                                                  condition: { ...cf.condition, value: variable.value }
                                                })}
                                                className={`px-2 py-1 text-xs rounded transition-colors ${
                                                  cf.condition.value === variable.value
                                                    ? 'bg-blue-500 text-white'
                                                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                                                }`}
                                                title={variable.description}
                                              >
                                                {variable.label}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                </div>

                                {cf.condition.operator === 'between' && (
                                  <input
                                    type="text"
                                    value={cf.condition.value2 ?? ''}
                                    onChange={(e) => updateConditionalFormat(column.field, cf.id, {
                                      condition: { ...cf.condition, value2: e.target.value }
                                    })}
                                    placeholder="Value 2 or variable"
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                                  />
                                )}
                              </div>
                            )}

                            {/* Color Presets */}
                            <div className="flex gap-1">
                              {PRESET_COLORS.map((preset, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => updateConditionalFormat(column.field, cf.id, {
                                    style: { backgroundColor: preset.bg, textColor: preset.text }
                                  })}
                                  className="w-8 h-8 rounded border-2 border-gray-200 hover:border-gray-400"
                                  style={{ backgroundColor: preset.bg }}
                                  title={preset.label}
                                />
                              ))}
                              <input
                                type="color"
                                value={cf.style.backgroundColor || '#ffffff'}
                                onChange={(e) => updateConditionalFormat(column.field, cf.id, {
                                  style: { ...cf.style, backgroundColor: e.target.value }
                                })}
                                className="w-8 h-8 rounded cursor-pointer"
                                title="Custom color"
                              />
                            </div>

                            {/* Preview */}
                            <div
                              className="mt-2 px-3 py-1.5 rounded text-sm"
                              style={{
                                backgroundColor: cf.style.backgroundColor,
                                color: cf.style.textColor,
                                fontWeight: cf.style.fontWeight
                              }}
                            >
                              Preview: Sample Value
                            </div>
                          </div>
                        ))}

                        {(column.conditionalFormats || []).length === 0 && (
                          <p className="text-xs text-gray-400 italic">
                            No conditional formatting rules. Click "Add Rule" to create one.
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] disabled:opacity-50"
          >
            {updateMutation.isPending ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
